<template>
	<!-- データ連携 -->
	<article v-if="isLink" class="profile-fitbit">
		<h1 class="profile-fitbit__title">睡眠・心拍<span class="is-period">{{ computedSummary.period }}</span></h1>

		<!-- ローディング -->
		<transition name="fade">
			<div v-if="isLoading" class="profile-fitbit__loading">
				<redraw-progress />
			</div>
		</transition>
		<!-- /ローディング -->

		<!-- エラー -->
		<div v-if="isError" class="profile-fitbit__error">
			<common-error-message>データを正常に<br>取得できませんでした</common-error-message>
		</div>
		<!-- /エラー -->

		<!-- プロフィールのFitbitサマリー -->
		<!-- NOTE: 他コンポーネントととは異なりエラーでも「--」表示で表示させる -->
		<div class="profile-fitbit__inner">
			<ul class="profile-fitbit__list">
				<li
					v-for="(record, index) in computedSummary.records"
					:key="index"
					class="profile-fitbit__item"
				>
					<a
						:href="`native://openFitbitReport?at=${record.date}`"
						class="profile-fitbit__link"
						:data-gtm-event-cat-label="`Fitbitサマリー${index + 1}日目_${isAndroid ? 'android' : 'iOS'}`"
						:data-gtm-event-act-label="`trackingFitbitSummary_days${index + 1}_${isAndroid ? 'android' : 'iOS'}`"
					>
						<!-- 曜日 -->
						<h2 v-if="index === 0" class="profile-fitbit__date is-today">今<br>日</h2>
						<h2 v-else class="profile-fitbit__date">{{ record.weekDay }}</h2>
						<!-- /曜日 -->

						<!-- 睡眠 -->
						<div class="profile-fitbit__sleep">
							<p class="profile-fitbit__sleep--total">
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<g clip-path="url(#clip0_819_2732)">
										<path d="M10.2667 6.45865C10.2987 6.49065 10.3147 6.53865 10.3093 6.58665L10.0267 8.23999C10.016 8.29332 10.0427 8.35199 10.0853 8.38399C10.128 8.41599 10.192 8.42132 10.24 8.39465L11.7227 7.61599C11.7653 7.59465 11.8133 7.59465 11.856 7.61599L13.3387 8.39465C13.3867 8.42132 13.4453 8.41599 13.4933 8.38399C13.536 8.35199 13.5627 8.29332 13.552 8.23999L13.2693 6.58665C13.2587 6.53865 13.2747 6.49065 13.312 6.45865L14.512 5.29065C14.5493 5.25332 14.5653 5.19465 14.5493 5.14132C14.5333 5.08799 14.4853 5.05065 14.432 5.03999L12.7733 4.79999C12.7253 4.79465 12.6827 4.76265 12.6613 4.71999L11.92 3.21599C11.8933 3.16799 11.8453 3.13599 11.7867 3.13599C11.728 3.13599 11.68 3.16799 11.6533 3.21599L10.912 4.71999C10.8907 4.76265 10.848 4.79465 10.8 4.79999L9.14133 5.03999C9.088 5.04532 9.04 5.08799 9.024 5.14132C9.008 5.19465 9.024 5.25332 9.06133 5.29065L10.2613 6.45865H10.2667Z" fill="#AB7DE7" />
										<path d="M13.8826 10.6987C11.36 12.1547 8.12797 11.248 6.73597 8.67201C5.41864 6.22935 6.33064 3.07201 8.74664 1.70668C9.40264 1.33868 10.1013 1.12535 10.8053 1.06135C10.944 1.05068 10.9706 0.858678 10.8373 0.810678C8.65064 -0.042655 6.09597 0.0960117 3.91997 1.41868C0.442641 3.53068 -0.842692 8.09601 1.02397 11.712C3.09331 15.7173 8.06398 17.1467 11.9253 14.9173C14.064 13.68 15.328 11.5787 15.7546 9.32268C15.7813 9.17334 15.6 9.10935 15.5146 9.22135C15.0986 9.80801 14.5493 10.3147 13.888 10.6987H13.8826Z" fill="#AB7DE7" />
									</g>
									<defs>
										<clipPath id="clip0_819_2732">
											<rect width="16" height="16" fill="white" />
										</clipPath>
									</defs>
								</svg>
								<span v-if=" record.sleep.totalMinute === '--'">--</span>
								<span v-else>{{ record.sleep.totalHour }}<span class="is-unit">時間</span>{{ record.sleep.totalMinute }}<span class="is-unit">分</span></span>
							</p>
							<p class="profile-fitbit__sleep--period">
								<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
									<g clip-path="url(#clip0_819_2738)">
										<path d="M5.9977 0C2.69069 0 0 2.69069 0 5.9977C0 9.30471 2.69069 11.9954 5.9977 11.9954C9.30471 11.9954 11.9954 9.30471 11.9954 5.9977C11.9954 2.69069 9.30471 0 5.9977 0ZM5.9977 10.5696C3.47719 10.5696 1.42583 8.51821 1.42583 5.9977C1.42583 3.47719 3.47719 1.43043 5.9977 1.43043C8.51821 1.43043 10.5696 3.48179 10.5696 6.0023C10.5696 8.52281 8.51821 10.5742 5.9977 10.5742V10.5696Z" fill="#4B4B4B" />
										<path d="M4.16251 8.51819C3.97853 8.51819 3.80835 8.4492 3.67957 8.32041C3.4128 8.05364 3.4128 7.62129 3.67957 7.35453L5.38597 5.64812L5.37217 3.2564C5.37217 2.87925 5.68034 2.57568 6.05289 2.57568C6.42545 2.57568 6.73361 2.88385 6.73361 3.2564V6.20466L4.64086 8.31581C4.51207 8.4446 4.34189 8.51359 4.15791 8.51359L4.16251 8.51819Z" fill="#4B4B4B" />
									</g>
									<defs>
										<clipPath id="clip0_819_2738">
										<rect width="12" height="12" fill="white" />
										</clipPath>
									</defs>
								</svg>
								<span>{{ record.sleep.period }}</span>
							</p>
						</div>
						<!-- /睡眠 -->

						<!-- 心拍 -->
						<div class="profile-fitbit__heartbeat">
							<p class="profile-fitbit__heartbeat--rate">
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M8 15.1657C8.08 15.1314 8.13143 15.1028 8.28572 15.0057C12.5371 12.1486 16 9.34856 16 5.8057C16 2.55999 14.1429 0.782844 11.8971 0.828558C10.0914 0.828558 8.98286 1.65142 8 2.87427C7.02286 1.65142 5.91429 0.828558 4.10286 0.828558C1.85714 0.788558 0 2.5657 0 5.81142C0 9.35427 3.46286 12.1486 7.71429 15.0114C7.87429 15.1086 7.92 15.1371 8 15.1714V15.1657Z" fill="#FF7373" />
								</svg>
								<span v-if=" record.heartbeat.rate === '--'">--</span>
								<span v-else>{{ record.heartbeat.rate }}<span class="is-unit">bpm</span></span>
							</p>
							<p class="profile-fitbit__heartbeat--max-min">
								<span class="is-unit">{{ `${record.heartbeat.max !== '--' ? '最大' : '' }` }}</span><span>{{ record.heartbeat.max }}</span><span class="is-unit">／</span><span class="is-unit">{{ `${record.heartbeat.min !== '--' ? '最小' : '' }` }}</span><span>{{ record.heartbeat.min }}</span>
							</p>
						</div>
						<!-- /心拍 -->

						<!-- 矢印 -->
						<i class="profile-fitbit__arrow">
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<g clip-path="url(#clip0_819_2775)">
									<path d="M7 4.00006L13 9.98395L7 16.0001" stroke="#1EBFBB" stroke-width="2" />
								</g>
								<defs>
									<clipPath id="clip0_819_2775">
										<rect width="20" height="20" fill="white" />
									</clipPath>
								</defs>
							</svg>
						</i>
						<!-- /矢印 -->
					</a>
				</li>
			</ul>
		</div>
		<!-- /プロフィールのFitbitサマリー -->
	</article>
	<!-- /データ連携 -->

	<!-- データ未連携 -->
	<aside v-else-if="isMount" class="profile-fitbit is-not-link">
		<img src="@/assets/img/fitbit/notlink_summary.png" alt="Fitbitサマリーイメージ">
		<a href="native://openFitbitInfo"><img src="@/assets/img/fitbit/notlink_summary_button.png" alt="Fitbitとデータ連携すると日々の睡眠・心拍データが詳細に確認できます！ 詳しくはこちら Fitbitとデータ連携して睡眠・心拍を確認！"></a>

		<!-- エラー -->
		<div v-if="isError" class="profile-fitbit__error">
			<common-error-message>データを正常に<br>取得できませんでした</common-error-message>
		</div>
		<!-- /エラー -->
	</aside>
	<!-- /データ未連携 -->
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_SUMMARY_DATA, SET_USER_INFO } from '../../store/modules/fitbit/mutation-types'

// Compornents
import RedrawProgress from '@/components/Molecules/RedrawProgress'
import CommonErrorMessage from '@/components/Molecules/CommonErrorMessage'

// ライブラリ
import moment from 'moment'
moment.locale('ja')

export default {
	name: 'ProfileFitbit',
	components: {
		RedrawProgress,
		CommonErrorMessage
	},
	data () {
		return {
			isLoading: false,
			isError: false,
			isLink: false,
			isMount: false
		}
	},
	mounted () {
		this.SET_USER_INFO({
			user_id: this.userId,
			user_token: this.userToken
		}).then(res => {
			this.isLink = this.userInfo.linked_flg
			if (this.isLink) this.getData()
		}).catch(error => {
			console.log('Error: ', error)
			this.isError = true
		}).finally(() => {
			this.isMount = true
		})
	},
	computed: {
		// map Vuex getters
		...mapGetters('common', ['userId', 'userToken', 'isAndroid']),
		...mapGetters('fitbit', ['summaryData', 'userInfo']),

		computedSummary () {
			const DAYS = 7	// 描画日数

			// 初期化
			const summaryData = {
				period: '--月--日～--月--日',
				records: []
			}
			for (let i = 0; i < DAYS; i++) {
				const objMoment = moment().hours(0).minute(0).second(0).millisecond(0).add(-i, 'days')
				const weekDay = objMoment.format('ddd')
				summaryData.records.push({
					date: objMoment.unix(),
					weekDay: weekDay,
					sleep: {
						totalHour: '--',
						totalMinute: '--',
						period: '--'
					},
					heartbeat: {
						rate: '--',
						max: '--',
						min: '--'
					}
				})
			}

			if (this.summaryData && this.summaryData.sleep && this.summaryData.heartrate) {
				const resSleep = this.summaryData.sleep
				const resHeatrate = this.summaryData.heartrate
				summaryData.period = moment(new Date(resSleep[DAYS - 1].date)).format('MM月DD日') + '～' + moment(new Date(resSleep[0].date)).format('MM月DD日')
				for (let i = 0; i < DAYS; i++) {
					const sleep = resSleep[i]
					const heartrate = resHeatrate[i]

					// データを整形
					const date = moment(new Date(sleep.date)).hours(0).minute(0).second(0).millisecond(0).unix()
					const weekDay = moment(new Date(sleep.date)).format('ddd')
					const hour = sleep.time_in_bed ? Math.floor(sleep.time_in_bed / 60) : '--'
					const minites = sleep.time_in_bed ? sleep.time_in_bed % 60 : '--'
					const period = sleep.start_time ? `${moment(new Date(sleep.start_time)).format('HH:mm')}～${moment(new Date(sleep.end_time)).format('HH:mm')}` : '--'
					const rate = heartrate.resting_heart_rate ? heartrate.resting_heart_rate : '--'
					const max = heartrate.max_heart_rate ? heartrate.max_heart_rate : '--'
					const min = heartrate.min_heart_rate ? heartrate.min_heart_rate : '--'

					// データを格納
					summaryData.records[i].date = date
					summaryData.records[i].weekDay = weekDay
					summaryData.records[i].sleep.totalHour = hour
					summaryData.records[i].sleep.totalMinute = minites
					summaryData.records[i].sleep.period = period
					summaryData.records[i].heartbeat.rate = rate
					summaryData.records[i].heartbeat.max = max
					summaryData.records[i].heartbeat.min = min
				}
			}

			return summaryData
		}
	},

	methods: {
		// map Vuex actions
		...mapActions('fitbit', [SET_SUMMARY_DATA, SET_USER_INFO]),

		getData () {
			this.isLoading = true

			// Fitbitサマリー取得API
			this.SET_SUMMARY_DATA({
				user_id: this.userId,
				user_token: this.userToken
			}).then(res => {
				// console.log('summaryData: ', this.summaryData)

				// API通信でOKでもエラーになるケース
				if (this.summaryData.records && this.summaryData.records.length !== 7) {
					console.log('Error: There is no daily data for 7 days.')
					this.isError = true	// データが7日分ないケースはエラーとする
				}
			}).catch(error => {
				// NOTE: 本画面はエラーでも表示するため、catch時はコンソールに出力するのみ
				console.log('Error: ', error)
			}).finally(() => {
				this.isLoading = false
			})
		}
	}
}
</script>

<style lang="scss" scoped>
@import "../../assets/sass/variable";

$font-size-15: 0.937rem;

$spacing-5: 0.312rem;
$spacing-12: 0.75rem;
$spacing-15: 0.937rem;

$color-futbit-primary: #5B9DFA;
$color-fitbit-secondary: #80B5FF;
$color-futbit-border: #EBEBEB;

.profile-fitbit {
	position: relative;
	padding: 0;
	font-family: $lp-family-primary;
	color: $text-primary;

	&__title {
		margin: 0 $spacing-10;
		padding: $spacing-8 $spacing-16 $spacing-2;
		background-color: $color-futbit-primary;
		font-family: $lp-family-primary;
		font-size: $font-size-15;
		color: $text-seconday;
		border-radius: $spacing-15 $spacing-15 0 0;

		.is-period {
			display: inline-block;
			margin-left: $spacing-6;
			font-size: $font-size-12;
		}
	}

	&__inner {
		min-height: 100px;
		margin: 0 $spacing-10;
		background-color: $background-primary;
		border-radius: 0 0 $spacing-15 $spacing-15;
	}

	&__list {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	&__item { border-bottom: 1px solid $color-futbit-border; }

	&__link {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 $spacing-5 0 0;
		text-decoration: none;
		color: $text-primary;
	}

	&__date {
		width: 26px;
		margin: 0;
		font-family: $family-primary;
		font-size: $font-size-12;
		text-align: center;
		line-height: $line-height-1;

		&.is-today { color: $primary; }
	}

	&__sleep,
	&__heartbeat {
		display: flex;
		align-items: center;
		flex-direction: column;
		flex: 1;
		box-sizing: border-box;
		font-family: $family-primary;
	}

	&__sleep {
		padding: $spacing-12 $spacing-16 $spacing-12 $spacing-6;

		&--total,
		&--period {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			width: 100%;
			margin: 0;

			svg { padding-bottom: $spacing-2; }
		}

		&--total {
			font-size: $font-size-16;

			& > span { margin-left: $spacing-4; }
			.is-unit { font-size: $font-size-12; }
		}

		&--period {
			margin: $spacing-4 0 0 0;
			font-size: $font-size-12;

			& > span { margin-left: $spacing-4; }
		}
	}

	&__heartbeat {
		padding: $spacing-12 $spacing-6 $spacing-12 $spacing-16;
		border-left: 1px solid $color-futbit-border;

		&--rate,
		&--max-min {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			width: 100%;
			margin: 0;
		}

		&--rate {
			font-size: $font-size-16;

			& > span { margin-left: $spacing-6; }
			.is-unit { font-size: $font-size-12; }
		}

		&--max-min {
			margin: $spacing-4 0 0 0;
			font-size: $font-size-12;

			.is-unit { font-size: $font-size-10; }
		}
	}

	&__arrow {
		display: block;
		width: 20px;
	}

	&__loading,
	&__error {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		overflow: hidden;
		z-index: 9999;
	}

	&.is-not-link {
		position: relative;
		padding: 0 $spacing-10;
		box-sizing: border-box;

		img {
			display: block;
			width: 100%;
		}

		a {
			display: block;
			width: 89.3334%;
			position: absolute;
			top: 25.266%;
			left: 50%;
			transform: translateX(-50%);
		}
	}
}

// アニメーション
.fade-enter-active, .fade-leave-active {
	transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}
</style>
